import { useState, useEffect } from 'react';
import { Box, Typography, TextField, InputAdornment, Button, ToggleButtonGroup, ToggleButton, Chip } from '@mui/material';
import { Search, LayoutGrid, List as ListIcon, Filter, GitCompare } from 'lucide-react';
import { FcLibrary } from 'react-icons/fc';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/navigation/PageHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { ErrorState } from '@/components/feedback/ErrorState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { tokens } from '@/theme/tokens';
import { useSchools } from '@/hooks/useSchools';
import { useSchool } from '@/hooks/useSchool';
import { SchoolCard } from './components/SchoolCard';

export function SchoolListPage() {
  usePageMeta('All Schools', [
    { label: 'Schools' },
    { label: 'All Schools' },
  ]);

  const navigate = useNavigate();
  const { switchSchool } = useSchool();
  const [viewMode, setViewMode] = useState('card');
  const [searchQuery, setSearchQuery] = useState('');
  
  const { schools, isLoading, error, refetch } = useSchools(searchQuery);

  useEffect(() => {
    if (error) {
      toast.error(error, { id: typeof error === 'string' ? error : 'school-list-error' });
    }
  }, [error]);

  const handleSwitchSchool = (schoolId) => {
    switchSchool(schoolId);
    navigate('/dashboard'); // Go to dashboard
  };

  const columns = [
    { 
      field: 'name', 
      headerName: 'School Name', 
      flex: 1, 
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
          <FcLibrary size={24} />
          <Typography variant="body2" fontWeight={500}>{params.value}</Typography>
        </Box>
      )
    },
    { field: 'code', headerName: 'School Code', width: 120 },
    { field: 'schoolType', headerName: 'Type', width: 130 },
    { field: 'city', headerName: 'City', width: 130 },
    { field: 'principalName', headerName: 'Principal', width: 150 },
    { 
      field: 'studentCount', 
      headerName: 'Students', 
      width: 100,
      renderCell: (params) => params?.value?.toLocaleString() || '0'
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small"
          color={params.value === 'Active' ? 'success' : 'default'}
          sx={{ fontWeight: 600, fontSize: '0.75rem' }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
          <Button 
            size="small" 
            variant="outlined" 
            onClick={() => handleSwitchSchool(params.row.id)}
          >
            Switch to School
          </Button>
          <Button 
            size="small"
            onClick={() => navigate(`/schools/${params.row.id}`)}
          >
            Details
          </Button>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ pb: 6 }}>
      <PageHeader
        title="School Management"
        subtitle="Manage, monitor, and compare all your branches from a single place."
        action={
          <Button
            variant="outlined"
            startIcon={<GitCompare size={18} />}
            onClick={() => navigate('/schools/compare')}
          >
            Compare Schools
          </Button>
        }
      />

      {/* Filters & View Mode Switcher */}
      <WidgetContainer sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <TextField
            placeholder="Search by school name, code, city, principal..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 350 }, bgcolor: 'white' }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color={tokens.colors.text.secondary} />
                  </InputAdornment>
                ),
              }
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button startIcon={<Filter size={18} />} color="inherit">
              Filters
            </Button>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size="small"
              sx={{ bgcolor: 'white' }}
            >
              <ToggleButton value="card" aria-label="card view">
                <LayoutGrid size={18} />
              </ToggleButton>
              <ToggleButton value="table" aria-label="table view">
                <ListIcon size={18} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </WidgetContainer>

      {/* Render View */}
      {error ? (
        <WidgetContainer sx={{ mt: 3, p: 4 }}>
          <ErrorState message={error} onRetry={refetch} />
        </WidgetContainer>
      ) : viewMode === 'card' ? (
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            }, 
            gap: 3,
            width: '100%'
          }}
        >
          {schools.map(school => (
            <SchoolCard 
              key={school.id}
              school={school} 
              onSwitch={handleSwitchSchool} 
            />
          ))}
          {schools.length === 0 && !isLoading && (
            <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 5, color: tokens.colors.text.secondary }}>
              <Typography>No schools found matching your search.</Typography>
            </Box>
          )}
        </Box>
      ) : (
        <WidgetContainer noPadding>
          <DataGrid
            autoHeight
            rows={schools}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            disableRowSelectionOnClick
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: false,
              },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
            }}
          />
        </WidgetContainer>
      )}
    </Box>
  );
}
